/**
 * F8.92: WalletCard - widget de billetera para el sidebar.
 * F11.92: Usa WalletContext global (SSE centralizado, sin estado local).
 */
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Wallet as WalletIcon, Gift, ChevronRight, AlertTriangle } from "lucide-react"
import { useWallet } from "../contexts/WalletContext"

function _formatPen(v) {
  if (v === null || v === undefined) return "S/ 0.00"
  return "S/ " + String(v)
}

function _badgeBono(estado) {
  switch (estado) {
    case "APTO":
      return {
        label: "Bono disponible",
        color: "text-emerald-700 bg-emerald-50 border-emerald-200",
        Icon: Gift,
      }
    case "YA_RECIBIDO":
      return {
        label: "Bono aplicado",
        color: "text-brand-700 bg-brand-50 border-brand-200",
        Icon: Gift,
      }
    case "SIN_SALDO_REAL":
      return {
        label: "Bono en riesgo",
        color: "text-amber-700 bg-amber-50 border-amber-200",
        Icon: AlertTriangle,
      }
    default:
      return null
  }
}

export default function WalletCard({ collapsed = false }) {
  const { data, loading } = useWallet()

  // Modo colapsado
  if (collapsed) {
    return (
      <Link
        to="/wallet"
        className="mx-2 mb-2 flex flex-col items-center gap-1 px-2 py-3 rounded-xl bg-gradient-to-br from-brand-50 to-surface-50 border border-brand-100 hover:border-brand-300 transition-colors"
        title={data ? `Saldo ${_formatPen(data.totalPen)}` : "Billetera"}
      >
        <WalletIcon className="w-5 h-5 text-brand-600" strokeWidth={2.2} />
        {data && (
          <div className="text-[10px] font-bold text-surface-900 tabular-nums">
            {_formatPen(data.totalPen).replace("S/ ", "")}
          </div>
        )}
      </Link>
    )
  }

  // Modo expandido
  const badge = data?.bonoMensual ? _badgeBono(data.bonoMensual.estado) : null

  return (
    <div className="mx-3 mb-3">
      <Link
        to="/wallet"
        className="block rounded-2xl bg-gradient-to-br from-brand-50 via-surface-50 to-white border border-brand-100 hover:border-brand-300 shadow-sm hover:shadow-card transition-all p-3.5 group"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-brand flex items-center justify-center shadow-sm">
              <WalletIcon className="w-4 h-4 text-white" strokeWidth={2.4} />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-surface-500">
              Billetera
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-surface-300 group-hover:text-brand-500 transition-colors" />
        </div>

        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-8 w-24 rounded bg-surface-100 animate-pulse"
            />
          ) : (
            <motion.div
              key="saldo"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-display text-2xl font-bold text-surface-900 tabular-nums leading-none"
            >
              {_formatPen(data?.totalPen)}
            </motion.div>
          )}
        </AnimatePresence>

        {data && !loading && (
          <div className="mt-2 flex items-center gap-3 text-[11px]">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-surface-500">
                Real{" "}
                <span className="font-semibold text-surface-700 tabular-nums">
                  {_formatPen(data.realPen)}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              <span className="text-surface-500">
                Bono{" "}
                <span className="font-semibold text-surface-700 tabular-nums">
                  {_formatPen(data.promoPen)}
                </span>
              </span>
            </div>
          </div>
        )}

        {badge && (
          <div
            className={`mt-2.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-semibold ${badge.color}`}
          >
            <badge.Icon className="w-3 h-3" strokeWidth={2.4} />
            {badge.label}
          </div>
        )}
      </Link>
    </div>
  )
}
